import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Checkbox,
  Collapse,
  Grid,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Print as PrintIcon,
  LockOutlined as LockOutlinedIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import VistaConduce from './VistaConduce';
import ConvertirConduceAFactura from './ConvertirConduceAFactura';

export const PROGRAMA_TRAZABILIDAD_CONDUCES = 'trazabilidadconduces';

const formatearFecha = (valor) => {
  if (!valor) return '—';
  const s = String(valor);
  const match = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return s;
};

const fmtMoney = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'A', label: 'Activo' },
  { value: 'P', label: 'Pendiente' },
  { value: 'D', label: 'Despachado' },
  { value: 'T', label: 'En tránsito' },
  { value: 'R', label: 'Recibido' },
];

const COLOR_ESTADO = {
  A: { bg: FP.color.surfaceAlt, fg: FP.color.textSecondary },
  P: { bg: FP.color.warningSoft, fg: FP.color.warning },
  D: { bg: FP.color.infoSoft, fg: FP.color.info },
  T: { bg: FP.color.warningSoft, fg: FP.color.warning },
  R: { bg: FP.color.successSoft, fg: FP.color.successDark },
};

const TrazabilidadConduce = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_TRAZABILIDAD_CONDUCES),
    [roleDetalles]
  );
  const puedeModificar = puedeLeer;

  const [conduces, setConduces] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [estadoEditando, setEstadoEditando] = useState(null);
  const [expandidoId, setExpandidoId] = useState(null);
  const [eliminandoItemId, setEliminandoItemId] = useState(null);
  const [conduceParaImprimir, setConduceParaImprimir] = useState(null);
  const [eliminandoConduceId, setEliminandoConduceId] = useState(null);
  const [dialogoConvertirOpen, setDialogoConvertirOpen] = useState(false);
  const [conducesAConvertir, setConducesAConvertir] = useState([]);

  const listarConduces = useCallback(async () => {
    setCargando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
      };
      if (filtroStatus) body.status = filtroStatus;
      if (fechaDesde) body.fecha_desde = fechaDesde;
      if (fechaHasta) body.fecha_hasta = fechaHasta;
      const response = await fetch(servidor + '/api/conducebackend_listarconduces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        setConduces(data.conduces || []);
        setSeleccionados([]);
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al listar conduces');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, filtroStatus, fechaDesde, fechaHasta, servidor, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (puedeLeer) listarConduces();
  }, [puedeLeer, listarConduces]);

  const actualizarEstado = async (conduceId, nuevoEstado) => {
    if (!puedeModificar) return;
    setEstadoEditando(conduceId);
    try {
      const response = await fetch(servidor + '/api/conducebackend_actualizarestado', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          conduce_id: conduceId,
          nuevo_estado: nuevoEstado,
        }),
      });
      if (response.ok) {
        showSuccess('Estado actualizado');
        listarConduces();
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al actualizar estado');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setEstadoEditando(null);
    }
  };

  const abrirDialogoConvertirFacturas = () => {
    const lista = seleccionados
      .map((id) => conduces.find((x) => x.id === id))
      .filter((c) => c && c.status === 'R' && !c.nfact);
    if (lista.length === 0) {
      showError('Seleccione al menos un conduce con estado Recibido');
      return;
    }
    if (!puedeModificar) {
      showError('No tiene permiso para convertir a facturas');
      return;
    }
    setConducesAConvertir(lista);
    setDialogoConvertirOpen(true);
  };

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const seleccionarTodosRecibidos = () => {
    const recibidos = conduces.filter((c) => c.status === 'R').map((c) => c.id);
    setSeleccionados((prev) => {
      const unidos = new Set([...prev, ...recibidos]);
      return Array.from(unidos);
    });
  };

  const recibidosSeleccionados = seleccionados.filter((id) => {
    const c = conduces.find((x) => x.id === id);
    return c && c.status === 'R';
  });

  const toggleExpandir = (conduceId) => {
    setExpandidoId((prev) => (prev === conduceId ? null : conduceId));
  };

  const eliminarItemConduce = async (conduceId, itemId) => {
    if (!puedeModificar) return;
    setEliminandoItemId(itemId);
    try {
      const response = await fetch(servidor + '/api/conducebackend_eliminaritemconduce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          conduce_id: conduceId,
          item_id: itemId,
        }),
      });
      if (response.ok) {
        showSuccess('Ítem eliminado del conduce');
        listarConduces();
        if (expandidoId === conduceId) setExpandidoId(null);
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al eliminar ítem');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setEliminandoItemId(null);
    }
  };

  const obtenerConfiguracionParaTipo = async (tipoDoc) => {
    const nombreTipoTrim = (tipoDoc || '').trim();
    const intentos = [];
    if (nombreTipoTrim) intentos.push(nombreTipoTrim);
    const upper = nombreTipoTrim.toUpperCase();
    if (upper && !intentos.includes(upper)) intentos.push(upper);
    for (const fallback of ['CONDUCE', 'Conduce']) {
      if (!intentos.includes(fallback)) intentos.push(fallback);
    }
    let ultimaRespuesta = null;
    for (const intento of intentos) {
      try {
        const response = await fetch(
          servidor + '/api/facturabackend_obtenerconfiguracion',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          }
        );
        if (!response.ok) continue;
        const data = await response.json();
        ultimaRespuesta = data?.configuracion || ultimaRespuesta;
        if (data?.configuracionGuardada && data?.configuracion) {
          return data.configuracion;
        }
      } catch (_) {
        // intento siguiente
      }
    }
    return ultimaRespuesta;
  };

  const datosConduceParaVista = (c, configuracion) => ({
    ...c,
    numero: c.id,
    fecha: c.fecha,
    cliente_nombre: c.cliente,
    direccionenvio: c.direccionenvio || '',
    observaciones: c.observaciones || '',
    empresa: selectedCompany,
    id_empresa: selectedCompany,
    configuracion,
    items: (c.items || []).map((it) => ({
      id: it.id,
      codigo: it.item,
      descripcion: it.descripcion || '—',
      cantidad: it.cantidad,
      unidad: it.unidad || 'UND',
      precio: it.precio || 0,
      subtotal: it.subtotal || 0,
      itbis: it.itbis || 0,
    })),
  });

  const abrirReimprimir = async (c, e) => {
    e.stopPropagation();
    const configuracion = await obtenerConfiguracionParaTipo('CONDUCE');
    setConduceParaImprimir(datosConduceParaVista(c, configuracion));
  };

  const eliminarConduce = async (c, e) => {
    e.stopPropagation();
    if (c.nfact || c.factura) {
      showError('No se puede eliminar un conduce con factura aplicada');
      return;
    }
    if (!puedeModificar) return;
    if (!window.confirm(`¿Eliminar el conduce ${c.id} completo?`)) return;
    setEliminandoConduceId(c.id);
    try {
      const response = await fetch(servidor + '/api/conducebackend_eliminarconduce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          conduce_id: c.id,
        }),
      });
      if (response.ok) {
        showSuccess('Conduce eliminado');
        listarConduces();
        if (expandidoId === c.id) setExpandidoId(null);
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al eliminar conduce');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setEliminandoConduceId(null);
    }
  };

  const handleLimpiarFiltros = () => {
    setFiltroStatus('');
    setFechaDesde('');
    setFechaHasta('');
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Trazabilidad de Conduces"
          subtitle={nombreNegocio ? `${nombreNegocio} · Seguimiento` : 'Seguimiento'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «{PROGRAMA_TRAZABILIDAD_CONDUCES}» en roles para acceder a trazabilidad de conduces.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Trazabilidad de Conduces"
        subtitle={nombreNegocio ? `${nombreNegocio} · Seguimiento` : 'Seguimiento'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Filtros */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="filtro-estado-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="filtro-estado-label"
                    value={filtroStatus}
                    label="Estado"
                    onChange={(e) => setFiltroStatus(e.target.value)}
                  >
                    {ESTADOS.map((e) => (
                      <MenuItem key={e.value || 'all'} value={e.value}>{e.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={listarConduces}
                    disabled={cargando}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                      '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                    }}
                  >
                    {cargando ? 'Cargando…' : 'Filtrar'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                  {puedeModificar && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={seleccionarTodosRecibidos}
                        sx={{
                          height: 46,
                          borderColor: FP.color.border,
                          color: FP.color.textPrimary,
                          bgcolor: '#FFFFFF',
                          borderRadius: 1.4,
                          textTransform: 'none',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                        }}
                      >
                        Seleccionar Recibidos
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<ReceiptIcon />}
                        onClick={abrirDialogoConvertirFacturas}
                        disabled={recibidosSeleccionados.length === 0}
                        sx={{
                          height: 46,
                          bgcolor: FP.color.success,
                          color: '#FFFFFF',
                          borderRadius: 1.4,
                          textTransform: 'none',
                          fontWeight: 700,
                          boxShadow: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: FP.color.successDark, boxShadow: 'none' },
                          '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                        }}
                      >
                        {`Convertir a factura(s) (${recibidosSeleccionados.length})`}
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', mt: 2 }}>
              Total de conduces encontrados: <strong>{conduces.length}</strong>
            </Typography>
          </CardContent>
        </Card>

        {/* Tabla de conduces */}
        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de conduces
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : conduces.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <LocalShippingIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Filtrar» para listar los conduces.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto', maxHeight: '60vh' }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: { xs: 1050, sm: 1300 } }}>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ ...cellHeadSx, width: 48 }} />
                      {puedeModificar && <TableCell padding="checkbox" sx={cellHeadSx}>Sel.</TableCell>}
                      <TableCell sx={cellHeadSx}>Nº</TableCell>
                      <TableCell sx={cellHeadSx}>Fecha</TableCell>
                      <TableCell sx={cellHeadSx}>Cliente</TableCell>
                      <TableCell sx={cellHeadSx}>Estado</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>Total</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>ITBIS</TableCell>
                      <TableCell sx={cellHeadSx}>Dirección envío</TableCell>
                      <TableCell sx={cellHeadSx}>Factura generada</TableCell>
                      <TableCell align="center" sx={cellHeadSx}>Acciones</TableCell>
                      {puedeModificar && <TableCell align="center" sx={cellHeadSx}>Cambiar estado</TableCell>}
                    </TableRow>
                    {conduces.map((c) => (
                      <React.Fragment key={c.id}>
                        <TableRow
                          hover
                          sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
                          onClick={() => toggleExpandir(c.id)}
                        >
                          <TableCell padding="checkbox" sx={{ width: 48 }}>
                            <IconButton size="small" aria-label={expandidoId === c.id ? 'Ocultar ítems' : 'Ver ítems'}>
                              {expandidoId === c.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          {puedeModificar && (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              {c.status === 'R' && (
                                <Checkbox
                                  disabled={!!c.nfact}
                                  checked={seleccionados.includes(c.id)}
                                  onChange={() => toggleSeleccion(c.id)}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{c.id}</TableCell>
                          <TableCell sx={{ color: FP.color.textSecondary }}>
                            {formatearFecha(c.fecha)}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: FP.color.textPrimary }}
                          >
                            {c.cliente || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={c.status_nombre || c.status}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                bgcolor: (COLOR_ESTADO[c.status] || COLOR_ESTADO.A).bg,
                                color: (COLOR_ESTADO[c.status] || COLOR_ESTADO.A).fg,
                              }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: FP.color.textPrimary }}
                          >
                            {fmtMoney(c.total)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontVariantNumeric: 'tabular-nums', color: FP.color.textSecondary }}
                          >
                            {fmtMoney(c.total_itbis)}
                          </TableCell>
                          <TableCell
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: FP.color.textSecondary }}
                            title={c.direccionenvio}
                          >
                            {c.direccionenvio || '—'}
                          </TableCell>
                          <TableCell sx={{ color: FP.color.textSecondary }}>
                            {c.nfact ? `${c.factura || ''} - ${c.nfact}` : '—'}
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Reimprimir conduce">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => abrirReimprimir(c, e)}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {puedeModificar && (
                              <Tooltip
                                title={
                                  c.nfact || c.factura
                                    ? 'No se puede eliminar: tiene factura aplicada'
                                    : 'Eliminar conduce completo'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => eliminarConduce(c, e)}
                                    disabled={!!(c.nfact || c.factura) || eliminandoConduceId === c.id || c.status === 'R'}
                                    sx={{
                                      color: FP.color.danger,
                                      '&:hover': { bgcolor: FP.color.dangerSoft },
                                      '&.Mui-disabled': { color: FP.color.textMuted },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                          {puedeModificar && (
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              {estadoEditando === c.id ? (
                                <CircularProgress size={18} sx={{ color: FP.color.accentBlue }} />
                              ) : (
                                <FormControl size="small" sx={{ minWidth: 140, ...fpInputSx, '& .MuiOutlinedInput-root': { ...fpInputSx['& .MuiOutlinedInput-root'], height: 32, minHeight: 32 } }}>
                                  <Select
                                    value={c.status || 'A'}
                                    onChange={(e) => actualizarEstado(c.id, e.target.value)}
                                    displayEmpty
                                    disabled={c.status === 'R'}
                                    sx={{ fontSize: '0.8rem' }}
                                  >
                                    {ESTADOS.filter((e) => e.value).map((opt) => (
                                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={puedeModificar ? 12 : 10} sx={{ py: 0, borderBottom: expandidoId === c.id ? undefined : 0 }}>
                            <Collapse in={expandidoId === c.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, bgcolor: FP.color.surfaceAlt, borderRadius: 1.5, my: 1 }}>
                                <Typography sx={{ mb: 1, fontWeight: 700, fontSize: '0.85rem', color: FP.color.textPrimary }}>
                                  Ítems del conduce
                                </Typography>
                                <Table size="small">
                                  <TableBody>
                                    <TableRow
                                      sx={{
                                        '& td': {
                                          border: 'none',
                                          borderBottom: `1px solid ${FP.color.border}`,
                                          bgcolor: FP.color.cardBg,
                                        },
                                      }}
                                    >
                                      <TableCell sx={cellHeadSx}>Código</TableCell>
                                      <TableCell sx={cellHeadSx}>Descripción</TableCell>
                                      <TableCell align="right" sx={cellHeadSx}>Cantidad</TableCell>
                                      <TableCell sx={cellHeadSx}>Unidad</TableCell>
                                      {puedeModificar && !c.nfact && <TableCell align="center" sx={cellHeadSx}>Eliminar</TableCell>}
                                    </TableRow>
                                    {(c.items || []).length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={puedeModificar && !c.nfact ? 5 : 4} sx={{ color: FP.color.textMuted }}>
                                          Sin ítems
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      (c.items || []).map((item) => (
                                        <TableRow key={item.id} hover>
                                          <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                                            {item.item || '—'}
                                          </TableCell>
                                          <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem' }}>
                                            {item.descripcion || '—'}
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {Number(item.cantidad)}
                                          </TableCell>
                                          <TableCell sx={{ color: FP.color.textSecondary }}>
                                            {item.unidad || 'UND'}
                                          </TableCell>
                                          {puedeModificar && !c.nfact && (
                                            <TableCell align="center">
                                              <IconButton
                                                size="small"
                                                onClick={() => eliminarItemConduce(c.id, item.id)}
                                                disabled={eliminandoItemId === item.id}
                                                sx={{
                                                  color: FP.color.danger,
                                                  '&:hover': { bgcolor: FP.color.dangerSoft },
                                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', mt: 2 }}>
              Solo los conduces con estado <strong>Recibido</strong> pueden convertirse a factura. Actualice el estado desde la columna &quot;Cambiar estado&quot;.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Vista previa / impresión */}
      <VistaConduce
        open={!!conduceParaImprimir}
        onClose={() => setConduceParaImprimir(null)}
        conduceData={conduceParaImprimir || {}}
      />

      <ConvertirConduceAFactura
        open={dialogoConvertirOpen}
        onClose={() => {
          setDialogoConvertirOpen(false);
          setConducesAConvertir([]);
          listarConduces();
        }}
        onExito={() => setSeleccionados([])}
        conduces={conducesAConvertir}
        puedeModificar={puedeModificar}
      />
    </Box>
  );
};

export default TrazabilidadConduce;
